import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full text-white antialiased bg-[linear-gradient(180deg,_#0d0b18_0%,_#16122c_50%,_#0b0914_100%)] relative overflow-x-hidden">
      {/* Soft, silk-like velvet glow overlay */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Terms & Conditions</h1>
              <p className="text-purple-400 text-sm">Terms of Service</p>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h1 className="text-3xl font-bold text-white mb-4">Terms & Conditions</h1>
              <p className="text-gray-400 mb-4">
                Last Updated: June 14, 2026
              </p>
              <p className="text-gray-300">
                By accessing and using SolveICSE, you agree to be bound by these Terms & Conditions. If you do not agree to these terms, please do not use our website.
              </p>
            </div>

            {/* Acceptance of Terms */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  By accessing or using SolveICSE, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions and our Privacy Policy.
                </p>
                <p>
                  If you do not agree with any part of these terms, you must not access our website or use our services.
                </p>
              </div>
            </div>

            {/* Use of Website */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">2. Use of Website</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-white font-medium mb-2">Permitted Use</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Access educational resources for personal, non-commercial use</li>
                    <li>Download and print materials for your own study purposes</li>
                    <li>Share resources with other students for educational purposes</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">Prohibited Use</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Reproducing or redistributing content for commercial purposes</li>
                    <li>Using automated tools to scrape or harvest data</li>
                    <li>Attempting to circumvent security measures or access restrictions</li>
                    <li>Uploading malicious content or viruses</li>
                    <li>Interfering with the operation of the website</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* User Account */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">3. User Accounts</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  To access certain features, you may be required to create an account. You are responsible for:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Notifying us immediately of any unauthorized use</li>
                  <li>Providing accurate and complete information</li>
                </ul>
                <p className="text-gray-400">
                  We reserve the right to suspend or terminate accounts that violate these terms.
                </p>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">4. Intellectual Property</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-white font-medium mb-2">Our Content</h3>
                  <p className="text-gray-400">
                    All content on SolveICSE, including but not limited to text, graphics, logos, images, and software, is our property or the property of our licensors and is protected by copyright laws.
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">Third-Party Content</h3>
                  <p className="text-gray-400">
                    Some educational materials, including past papers and solutions, are sourced from official examination boards and educational institutions. These materials are used for educational purposes under fair use principles.
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">User Content</h3>
                  <p className="text-gray-400">
                    By submitting content to our platform, you grant us a non-exclusive, royalty-free license to use, display, and distribute your content for the purposes of operating our website.
                  </p>
                </div>
              </div>
            </div>

            {/* Educational Content Disclaimer */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">5. Educational Content Disclaimer</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  <strong className="text-purple-400">Important:</strong> The educational materials, past papers, and solutions provided on SolveICSE are for reference purposes only.
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>We strive for accuracy but cannot guarantee that all content is error-free</li>
                  <li>Past papers and solutions may not reflect the most current examination patterns</li>
                  <li>Always verify information with official sources and your teachers</li>
                  <li>We are not affiliated with CISCE or any examination board</li>
                  <li>Content should be used as supplementary study material only</li>
                </ul>
                <p className="text-gray-400">
                  We do not accept liability for any loss or damage resulting from the use of our educational materials.
                </p>
              </div>
            </div>

            {/* User Conduct */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">6. User Conduct</h2>
              <div className="space-y-4 text-gray-300">
                <p>Users agree not to:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>Engage in harassment, abuse, or threatening behavior</li>
                  <li>Post inappropriate or offensive content</li>
                  <li>Impersonate any person or entity</li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe upon the rights of others</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                </ul>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">7. Limitation of Liability</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  To the fullest extent permitted by law, SolveICSE shall not be liable for:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>Any indirect, incidental, special, or consequential damages</li>
                  <li>Loss of data, profits, or business opportunities</li>
                  <li>System downtime or service interruptions</li>
                  <li>Errors or inaccuracies in educational content</li>
                  <li>Actions of third parties or external websites</li>
                </ul>
                <p className="text-gray-400">
                  In no event shall our total liability exceed the amount you paid, if any, for accessing our services.
                </p>
              </div>
            </div>

            {/* Privacy */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">8. Privacy</h2>
              <p className="text-gray-300">
                Your use of SolveICSE is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal information. By using our website, you consent to our data practices as described in the Privacy Policy.
              </p>
            </div>

            {/* Modifications */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">9. Modifications</h2>
              <p className="text-gray-300">
                We reserve the right to modify these Terms & Conditions at any time. Changes will be effective immediately upon posting on this website. Your continued use of our services after such modifications constitutes your acceptance of the updated terms.
              </p>
            </div>

            {/* Termination */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">10. Termination</h2>
              <p className="text-gray-300">
                We reserve the right to terminate or suspend your access to our website at any time, without prior notice, for any reason, including but not limited to violation of these Terms & Conditions.
              </p>
            </div>

            {/* Governing Law */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">11. Governing Law</h2>
              <p className="text-gray-300">
                These Terms & Conditions shall be governed by and construed in accordance with the laws of India. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in India.
              </p>
            </div>

            {/* Contact Information */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">12. Contact Us</h2>
              <p className="text-gray-300 mb-4">
                If you have any questions about these Terms & Conditions, please contact us:
              </p>
              <div className="space-y-2 text-gray-400">
                <p>Email: <a href="mailto:devajithmohanmohan@gmail.com" className="text-purple-400 hover:underline">devajithmohanmohan@gmail.com</a></p>
                <p>Website: <a href="https://solveicse.dev" className="text-purple-400 hover:underline">https://solveicse.dev</a></p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
